<template>
    <Card class="inventory-manager-card">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_INVENTORY_MANAGER">
            <div>
                <div class="inventory-header">
                    <div class="left-section">
                        <div class="checkbox-container">
                            <label>
                                <input type="checkbox" class="form-check-input" v-model="outOfStock" />
                                Show packages/LOT with no stock
                            </label>
                        </div>
                        <div class="results-count">Total Results: {{ totalResults }}</div>
                    </div>
                    <div class="export-section" v-if="warehouse">
                        <Button
                            :noMargin="true"
                            type="button"
                            class="export-button"
                            :disabled="exportingInventory"
                            @click="toggleExportMenu"
                        >
                            <i class="material-icons">file_download</i>
                            <span>{{ exportingInventory ? "Exporting..." : "Export" }}</span>
                            <i class="material-icons">arrow_drop_down</i>
                        </Button>
                        <div v-if="showExportMenu" class="export-menu">
                            <button type="button" @click="exportInventory('current')">
                                <strong>Current filtered inventory</strong>
                                <span>Matches the location, room, category, search, and zero-stock toggle shown here.</span>
                            </button>
                            <button type="button" @click="exportInventory('all')">
                                <strong>All on-hand inventory for location</strong>
                                <span>Full location snapshot with all rooms, excluding zero-stock packages.</span>
                            </button>
                            <button type="button" @click="exportInventory('zeroStock')">
                                <strong>Zero-stock packages/LOTs</strong>
                                <span>Only packages/LOTs with no stock for the selected location.</span>
                            </button>
                            <button type="button" @click="openStockHistoryExportModal">
                                <strong>Stock movement history</strong>
                                <span>Adjustments, transfers, receives, room moves, and optional sales for a date range.</span>
                            </button>
                        </div>
                    </div>
                    
                    <form class="search-section" @submit.prevent="runSearchNow">
                        <input
                            type="text"
                            name="search"
                            class="form-control"
                            v-model="searchStr"
                            placeholder="Search Items"
                        />
                        <select class="form-select" name="item_group" v-model="itemGroup">
                            <option value="" selected>All Categories</option>
                            <option :value="itemGroup.name" v-for="itemGroup of itemGroups" :key="itemGroup.id">
                                {{ itemGroup.name }}
                            </option>
                        </select>
                        <select class="form-select" name="item_group" v-model="warehouse">
                            <option value="" selected>All Warehouses</option>
                            <option :value="wr.id" v-for="wr of warehouses" :key="wr.id">
                                {{ wr.name }}
                            </option>
                        </select>
                        <select class="form-select" name="warehouse_room" v-model="warehouseRoom">
                            <option value="" selected>All Rooms</option>
                            <option :value="wr.id" v-for="wr of warehouseRoomOptionsForSelect" :key="wr.id">
                                {{ wr.title }}
                            </option>
                        </select>
                        <Button
                            v-if="warehouse"
                            :noMargin="true"
                            type="button"
                            class="add-room-button"
                            title="Add inventory room"
                            @click="openCreateRoomModal"
                        >
                            <i class="material-icons">add</i>
                            <span>Add room</span>
                        </Button>
                        <div class="sort-dropdown">
                            <select class="form-select" name="sort_by" v-model="sortBy">
                                <option value="name_asc" selected>Alphabetical (A-Z)</option>
                                <option value="name_desc">Alphabetical (Z-A)</option>
                                <option value="date_asc">Date Added (Oldest First)</option>
                                <option value="date_desc">Date Added (Newest First)</option>
                            </select>
                            <div class="sort-label">
                                <span v-if="sortBy === 'name_asc'">Alphabetical (A-Z)</span>
                                <span v-else-if="sortBy === 'name_desc'">Alphabetical (Z-A)</span>
                                <span v-else-if="sortBy === 'date_asc'">Date Added (Oldest First)</span>
                                <span v-else-if="sortBy === 'date_desc'">Date Added (Newest First)</span>
                                <i class="material-icons">arrow_drop_down</i>
                            </div>
                        </div>
                        <Button type="submit" :noMargin="true" class="search-button">{{ searching ? "Please wait..." : "Search" }}</Button>
                    </form>
                </div>

                <UiState v-if="loading" type="loading" title="Loading inventory" />
                <div class="inventory-content" v-else>
                    <UiState
                        v-if="!products.length"
                        type="empty"
                        title="No products found"
                        message="Try changing your search or filter criteria."
                    />

                    <div v-else class="inventory-table-scroll" tabindex="0" aria-label="Inventory results table">
                        <table class="inventory-table">
                            <thead>
                            <tr>
                                <th class="image-column"></th>
                                <th>Name</th>
                                <th>In Stock</th>
                                <th>Price</th>
                                <th>Category</th>
                                <th>Suppliers</th>
                                <th>Manufacturer</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="product of products" :key="product.id">
                                <td>
                                    <div class="product-image">
                                        <img
                                            v-if="product.image && !product.imageLoadFailed"
                                            :src="getServerAssetUrl(product.image)"
                                            alt="Product thumbnail"
                                            @error="markImageLoadFailed(product)"
                                        />
                                        <div v-else class="no-image">No Image</div>
                                    </div>
                                </td>
                                <td>
                                    <router-link
                                        target="_blank"
                                        :to="productDetailPath(product.parent)"
                                    >
                                        {{ product.name }}
                                    </router-link>
                                </td>
                                <td>
                                    <StockBreakdownView
                                        :showBackRoomStock="warehouseRoom == 'BACK_ROOM'"
                                        :totalFrontRoomStock="product.totalFrontRoomStock"
                                        :totalBackRoomStock="product.totalBackRoomStock"
                                        :totalCustomRoomStock="product.totalCustomRoomStock"
                                        :totalStock="product.stock"
                                        :showAllStock="!warehouseRoom"
                                        :selectedRoomKey="warehouseRoom"
                                        :selectedRoomName="selectedWarehouseRoomName"
                                        :warehouseEntries="product.warehouseEntries"
                                    />
                                </td>
                                <td>{{ product.selling_price }}</td>
                                <td>{{ product.item_group }}</td>
                                <td>{{ product.supplier_sku_name }}</td>
                                <td>{{ product.manufacturer }}</td>
                                <td>
                                    <div class="inventory-row-actions">
                                        <InventoryMoveModal @update="handleStockMoveUpdate" :variant="product" />
                                        <AllowPermission :permission="PERMISSION_CODES.CMS_EDIT_INVENTORY_MANAGER">
                                            <PackageIdEditModal
                                                :variant="product"
                                                :selectedWarehouseId="warehouse"
                                                :selectedRoomKey="warehouseRoom"
                                                @update="handleStockMoveUpdate"
                                            />
                                        </AllowPermission>
                                        <AllowPermission :permission="PERMISSION_CODES.CMS_PRINT_LABEL" span>
                                            <router-link
                                                class="inventory-label-action"
                                                :to="printLabelsProductPath(product.id)"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                :aria-label="`Print labels for ${product.name}`"
                                            >
                                                <i class="material-icons" aria-hidden="true">print</i>
                                                <span>Print labels</span>
                                            </router-link>
                                        </AllowPermission>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="pagination-footer">
                    <div class="pagination-controls">
                        <Paginator :totalPages="totalPages" :page="page" @change="page = $event" />
                    </div>
                    <label class="rows-per-page-control" for="inventory-rows-per-page">
                        <span>Rows per page</span>
                        <select
                            id="inventory-rows-per-page"
                            class="form-select"
                            :value="limit"
                            @change="changePageSize($event.target.value)"
                        >
                            <option v-for="option of pageSizeOptions" :key="option" :value="option">
                                {{ option }}
                            </option>
                        </select>
                    </label>
                </div>
                <CustomModal
                    title="Manage inventory rooms"
                    :show="showCreateRoomModal"
                    @close="closeCreateRoomModal"
                >
                    <div class="room-modal-form">
                        <div class="selected-location">
                            <span>Location</span>
                            <strong>{{ selectedWarehouseName }}</strong>
                        </div>

                        <div class="room-list">
                            <div class="room-list-header">
                                <span>Rooms</span>
                                <small>{{ warehouseRoomOptionsForSelect.length }} total</small>
                            </div>
                            <div
                                class="room-list-item"
                                v-for="room of warehouseRoomOptionsForSelect"
                                :key="room.roomId || room.id"
                            >
                                <div>
                                    <strong>{{ room.title }}</strong>
                                    <span v-if="room.isDefault || room.id === 'FRONT_ROOM' || room.id === 'BACK_ROOM'">
                                        Default room
                                    </span>
                                    <span v-else>Custom room</span>
                                </div>
                                <Button
                                    v-if="!(room.isDefault || room.id === 'FRONT_ROOM' || room.id === 'BACK_ROOM')"
                                    :noMargin="true"
                                    type="button"
                                    class="delete-room-button"
                                    :disabled="deletingRoomId === (room.roomId || room.id)"
                                    @click="deleteWarehouseRoom(room)"
                                >
                                    {{ deletingRoomId === (room.roomId || room.id) ? "Deleting..." : "Delete" }}
                                </Button>
                                <span v-else class="locked-room-label">Locked</span>
                            </div>
                        </div>

                        <form class="room-create-form" @submit.prevent="createWarehouseRoom">
                            <label for="new-room-name">Add room</label>
                            <div class="room-create-row">
                                <input
                                    id="new-room-name"
                                    type="text"
                                    class="form-control"
                                    v-model.trim="newRoomName"
                                    placeholder="Vault room"
                                    autocomplete="off"
                                />
                                <Button
                                    :noMargin="true"
                                    type="submit"
                                    :disabled="creatingRoom || !newRoomName"
                                >
                                    {{ creatingRoom ? "Creating..." : "Create" }}
                                </Button>
                            </div>
                            <p class="room-modal-help">
                                Room names are saved only for this location.
                            </p>
                        </form>

                        <div class="room-modal-actions">
                            <Button
                                :noMargin="true"
                                type="button"
                                class="secondary-room-button"
                                @click="closeCreateRoomModal"
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                </CustomModal>
                <CustomModal
                    title="Export stock movement history"
                    :show="showHistoryExportModal"
                    @close="closeStockHistoryExportModal"
                >
                    <div class="history-export-form">
                        <div class="selected-location">
                            <span>Location</span>
                            <strong>{{ selectedWarehouseName }}</strong>
                        </div>

                        <div class="history-export-grid">
                            <label>
                                <span>Start date</span>
                                <input type="date" class="form-control" v-model="historyExport.startDate" />
                            </label>
                            <label>
                                <span>End date</span>
                                <input type="date" class="form-control" v-model="historyExport.endDate" />
                            </label>
                            <label>
                                <span>Event type</span>
                                <select class="form-select" v-model="historyExport.eventCode">
                                    <option value="">All movement types</option>
                                    <option value="ASN_IMPORT">ASN Import</option>
                                    <option value="STOCK_TRANSFER">Stock Transfer</option>
                                    <option value="QTY_ADJUSTMENT">Qty Adjustment</option>
                                    <option value="ROOM_MOVE">Room Movement</option>
                                    <option value="PACKAGE_ID_CHANGE">Package ID Change</option>
                                    <option value="SALE">Sales</option>
                                </select>
                            </label>
                        </div>

                        <label class="history-checkbox">
                            <input type="checkbox" class="form-check-input" v-model="historyExport.includeSales" />
                            Include sales deductions when exporting all movement types
                        </label>

                        <div class="history-export-actions">
                            <Button
                                :noMargin="true"
                                type="button"
                                class="secondary-room-button"
                                @click="closeStockHistoryExportModal"
                            >
                                Cancel
                            </Button>
                            <Button
                                :noMargin="true"
                                type="button"
                                :disabled="exportingInventory"
                                @click="exportStockHistory"
                            >
                                {{ exportingInventory ? "Exporting..." : "Download history CSV" }}
                            </Button>
                        </div>
                    </div>
                </CustomModal>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { http } from "@/utils/http"
import Button from "../../components/UI/Button.vue"
import { mapActions, mapState } from "vuex"
import StockBreakdownView from "../../components/inventory/StockBreakdownView.vue"
import { confirmDialog, getServerAssetUrl } from "../../utils/common"
import { downloadCSV } from "../../utils/csv-download"
import Paginator from "../../components/other/Paginator.vue"
import InventoryMoveModal from "../../components/inventory/InventoryMoveModal.vue"
import PackageIdEditModal from "../../components/inventory/PackageIdEditModal.vue"
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import CustomModal from "../../components/layout/CustomModal.vue"
import { PERMISSION_CODES } from "../../store/permission"
import UiState from "../../components/UI/UiState.vue"
import { printLabelsProductPath, productDetailPath } from "../../router/route-paths"

const INVENTORY_EXPORT_COLUMNS = [
    "Location",
    "Product",
    "SKU",
    "Category",
    "Supplier",
    "Manufacturer",
    "LOT",
    "Total Units",
    "Front Room",
    "Back Room",
    "Custom Rooms",
    "Price",
]

const STOCK_HISTORY_EXPORT_COLUMNS = [
    "Date",
    "Location",
    "Event",
    "Direction",
    "Quantity",
    "Back Room Quantity",
    "Product",
    "SKU",
    "LOT",
    "Reason",
    "Reference Type",
    "Reference",
    "User",
]

export default {
    data() {
        return {
            loading: false,
            outOfStock: false,
            products: [],
            searchStr: "",
            itemGroup: "",
            searching: false,
            totalPages: 0,
            page: 1,
            limit: 50,
            pageSizeOptions: [10, 25, 50, 100],
            warehouse: "",
            warehouseRoom: "",
            sortBy: "name_asc",
            PERMISSION_CODES,
            totalResults: 0,
            exportingInventory: false,
            warehouseRoomOptions: [],
            newRoomName: "",
            creatingRoom: false,
            deletingRoomId: "",
            showCreateRoomModal: false,
            showExportMenu: false,
            showHistoryExportModal: false,
            historyExport: {
                startDate: "",
                endDate: "",
                eventCode: "",
                includeSales: false,
            },
            searchDebounceTimer: null,
            fetchRequestId: 0,
        }
    },
    components: {
        InventoryMoveModal,
        PackageIdEditModal,
        Paginator,
        StockBreakdownView,
        Card,
        Button,
        AllowPermission,
        CustomModal,
        UiState,
    },
    computed: {
        ...mapState("misc", ["itemGroups", "warehouses", "warehouseRooms"]),
        selectedWarehouseName() {
            const selectedWarehouse = this.warehouses.find((item) => item.id == this.warehouse)
            return selectedWarehouse ? selectedWarehouse.name : "Selected Location"
        },
        warehouseRoomOptionsForSelect() {
            return this.warehouseRoomOptions.length ? this.warehouseRoomOptions : Object.values(this.warehouseRooms)
        },
        selectedWarehouseRoomName() {
            const selectedRoom = this.warehouseRoomOptionsForSelect.find((item) => item.id === this.warehouseRoom)
            return selectedRoom ? selectedRoom.title : ""
        },
        selectedWarehouseSlugName() {
            return this.selectedWarehouseName || "Selected Location"
        },
    },
    mounted() {
        this.setData({
            pageTitle: "Inventory Manager",
        })
        this.itemGroup = this.$route.query.item_group || ""
        this.searchStr = this.$route.query.search || ""
        this.fetchProducts()
    },
    watch: {
        outOfStock() {
            this.page = 1
            this.fetchProducts()
        },
        page() {
            this.fetchProducts(this.selectedCategory || "")
        },
        sortBy() {
            this.page = 1
            this.fetchProducts()
        },
        searchStr() {
            this.queueSearch()
        },
        warehouse() {
            this.page = 1
            this.warehouseRoom = ""
            this.fetchWarehouseRooms()
            this.fetchProducts()
        },
        warehouseRoom() {
            this.page = 1
            this.fetchProducts()
        },
    },
    beforeDestroy() {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        printLabelsProductPath,
        productDetailPath,
        getServerAssetUrl,
        handleStockMoveUpdate() {
            // Mutation responses contain all-location totals; reload the active
            // filters so location/room counts and zero-stock membership stay valid.
            return this.fetchProducts(this.selectedCategory || "")
        },
        markImageLoadFailed(product) {
            this.$set(product, "imageLoadFailed", true)
        },
        changePageSize(value) {
            const nextLimit = Number(value)
            if (!this.pageSizeOptions.includes(nextLimit) || nextLimit === this.limit) return

            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
                this.searchDebounceTimer = null
            }
            this.limit = nextLimit
            if (this.page !== 1) {
                this.page = 1
                return
            }
            this.fetchProducts()
        },
        toggleExportMenu() {
            if (this.exportingInventory) return
            this.showExportMenu = !this.showExportMenu
        },
        closeExportMenu() {
            this.showExportMenu = false
        },
        openStockHistoryExportModal() {
            this.closeExportMenu()
            this.showHistoryExportModal = true
        },
        closeStockHistoryExportModal() {
            if (this.exportingInventory) return
            this.showHistoryExportModal = false
        },
        todayFileStamp() {
            const date = new Date()
            const pad = (value) => String(value).padStart(2, "0")
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(
                date.getMinutes()
            )}`
        },
        exportFileName(parts) {
            return ["high-ties", ...parts.filter(Boolean), this.todayFileStamp()].join(" - ")
        },
        inventoryExportLabel(type) {
            if (type === "all") return "all on-hand inventory"
            if (type === "zeroStock") return "zero-stock inventory"
            return this.outOfStock ? "current filtered inventory with zero-stock" : "current filtered inventory"
        },
        buildInventoryExportQuery(type) {
            const params = new URLSearchParams({
                variants: "true",
                page: "1",
                limit: "99999",
                warehouse: this.warehouse,
                sort: this.sortBy,
            })

            if (type === "all") {
                params.set("out_of_stock", "0")
                return params.toString()
            }

            if (type === "zeroStock") {
                params.set("out_of_stock", "1")
                params.set("show_only_out_of_stock", "1")
            } else {
                params.set("out_of_stock", this.outOfStock ? "1" : "0")
            }

            if (this.searchStr) params.set("search", this.searchStr)
            if (this.itemGroup) params.set("item_group", this.itemGroup)
            if (this.warehouseRoom) params.set("warehouse_room", this.warehouseRoom)

            return params.toString()
        },
        queueSearch() {
            this.fetchRequestId += 1
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.searchDebounceTimer = null
                this.page = 1
                this.fetchProducts()
            }, 400)
        },
        runSearchNow() {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
                this.searchDebounceTimer = null
            }
            this.page = 1
            this.fetchProducts()
        },
        async fetchWarehouseRooms() {
            if (!this.warehouse) {
                this.warehouseRoomOptions = Object.values(this.warehouseRooms)
                return
            }
            try {
                const res = await http.get(`/stock/rooms?warehouse_id=${this.warehouse}`)
                this.warehouseRoomOptions = res.data.result || Object.values(this.warehouseRooms)
            } catch (err) {
                console.log("error in loading warehouse rooms", err)
                this.warehouseRoomOptions = Object.values(this.warehouseRooms)
            }
        },
        openCreateRoomModal() {
            this.newRoomName = ""
            this.showCreateRoomModal = true
            this.$nextTick(() => {
                const input = document.getElementById("new-room-name")
                if (input) input.focus()
            })
        },
        closeCreateRoomModal() {
            if (this.creatingRoom) return
            this.showCreateRoomModal = false
            this.newRoomName = ""
        },
        async createWarehouseRoom() {
            if (!this.warehouse) {
                this.$toast.error("Select a warehouse before adding a room.")
                return
            }
            if (!this.newRoomName) return
            const normalizedRoomName = this.newRoomName.trim().toLowerCase()
            if (["front room", "back room"].includes(normalizedRoomName)) {
                this.$toast.error("Front Room and Back Room already exist.")
                return
            }
            const duplicateRoom = this.warehouseRoomOptionsForSelect.some((room) => {
                return String(room.title || "").trim().toLowerCase() === normalizedRoomName
            })
            if (duplicateRoom) {
                this.$toast.error("A room with this name already exists for this location.")
                return
            }
            this.creatingRoom = true
            try {
                const res = await http.post("/stock/rooms", {
                    warehouse_id: this.warehouse,
                    name: this.newRoomName,
                })
                await this.fetchWarehouseRooms()
                this.warehouseRoom = res.data.result.id
                this.newRoomName = ""
                this.fetchProducts()
            } catch (err) {
                console.log("error creating warehouse room", err)
                const message = err.response && err.response.data && err.response.data.message
                this.$toast.error(message || "Room could not be added.")
            }
            this.creatingRoom = false
        },
        async deleteWarehouseRoom(room) {
            const roomIdentifier = room.roomId || room.id
            if (!roomIdentifier) return
            const confirmed = await confirmDialog({
                title: `Delete ${room.title}?`,
                text: "This room will be removed from the selected location.",
                confirmButtonText: "Delete room",
            })
            if (!confirmed) return

            this.deletingRoomId = roomIdentifier
            try {
                await http.delete(`/stock/rooms/${roomIdentifier}?warehouse_id=${this.warehouse}`)
                if (this.warehouseRoom === room.id || this.warehouseRoom === room.roomKey) {
                    this.warehouseRoom = ""
                }
                await this.fetchWarehouseRooms()
                this.fetchProducts()
            } catch (err) {
                console.log("error deleting warehouse room", err)
                const message = err.response && err.response.data && err.response.data.message
                this.$toast.error(message || "Room could not be deleted.")
            }
            this.deletingRoomId = ""
        },

        async fetchProducts(val = "") {
            const requestId = ++this.fetchRequestId
            this.searching = true
            this.selectedCategory = val
            this.loading = true
            try {
                const res = await http.get(
                    `/stock/inventory?variants=true&search=${this.searchStr}&item_group=${this.itemGroup}&out_of_stock=${
                        this.outOfStock ? 1 : 0
                    }&page=${this.page}&limit=${this.limit}&warehouse=${this.warehouse}&warehouse_room=${this.warehouseRoom}&sort=${this.sortBy}`
                )

                if (requestId !== this.fetchRequestId) {
                    return
                }
                
                this.products = res.data.result
                this.totalPages = res.data.totalPages
                this.totalResults = res.data.totalResults
            } catch (err) {
                if (requestId !== this.fetchRequestId) {
                    return
                }
                console.log("error in loading items", err)
            }
            if (requestId === this.fetchRequestId) {
                this.loading = false
                this.searching = false
            }
        },
        flattenInventoryRows(products) {
            const rows = []
            products.forEach((product) => {
                (product.warehouseEntries || []).forEach((warehouseEntry) => {
                    (warehouseEntry.batches || []).forEach((batch) => {
                        rows.push({
                            Location: warehouseEntry.warehouse ? warehouseEntry.warehouse.name : "",
                            Product: product.name,
                            SKU: product.item_code || product.catalog_sku || "",
                            Category: product.item_group || "",
                            Supplier: product.supplier_sku_name || "",
                            Manufacturer: product.manufacturer || "",
                            LOT: batch.batch_no || "",
                            "Total Units": batch.qty || 0,
                            "Front Room": batch.frontRoomQty || 0,
                            "Back Room": batch.backRoomQty || 0,
                            "Custom Rooms": (batch.customRooms || []).map((room) => `${room.name}: ${room.qty}`).join("; "),
                            Price: product.selling_price || "",
                        })
                    })
                })
            })
            return rows
        },
        flattenStockHistoryRows(logs) {
            return logs.map((log) => ({
                Date: log.createdAtFormatted || log.createdAtEstFormatted || log.createdAt || "",
                Location: log.warehouse_name || "",
                Event: log.eventName || log.eventCode || "",
                Direction: log.isAddition ? "Added" : "Removed",
                Quantity: log.qty || 0,
                "Back Room Quantity": log.backRoomQty || 0,
                Product: log.item_name || "",
                SKU: log.item_code || "",
                LOT: log.batch_no || "",
                Reason: log.qtyAdjustmentReasonCode || "",
                "Reference Type": log.ref_type || "",
                Reference: log.ref_name || log.ref_id || "",
                User: log.modified_by || "",
            }))
        },
        async exportInventory(type = "current") {
            if (!this.warehouse) {
                this.$toast.error("Select a warehouse before exporting inventory.")
                return
            }
            this.closeExportMenu()
            this.exportingInventory = true
            try {
                const res = await http.get(`/stock/inventory?${this.buildInventoryExportQuery(type)}`)
                const rows = this.flattenInventoryRows(res.data.result || [])
                if (!rows.length) {
                    this.$toast.error("No inventory rows available to export.")
                    return
                }
                const roomLabel = type === "all" ? "all rooms" : this.selectedWarehouseRoomName || "all rooms"
                downloadCSV(
                    rows,
                    this.exportFileName([this.selectedWarehouseSlugName, roomLabel, this.inventoryExportLabel(type)]),
                    INVENTORY_EXPORT_COLUMNS
                )
            } catch (err) {
                console.log("error exporting inventory", err)
                this.$toast.error("Inventory export failed.")
            } finally {
                this.exportingInventory = false
            }
        },
        async exportStockHistory() {
            if (!this.warehouse) {
                this.$toast.error("Select a warehouse before exporting stock history.")
                return
            }
            if (
                this.historyExport.startDate &&
                this.historyExport.endDate &&
                this.historyExport.startDate > this.historyExport.endDate
            ) {
                this.$toast.error("Start date must be before the end date.")
                return
            }
            this.exportingInventory = true
            try {
                const params = new URLSearchParams({
                    page: "1",
                    limit: "99999",
                })
                if (this.historyExport.startDate) params.set("start_date", this.historyExport.startDate)
                if (this.historyExport.endDate) params.set("end_date", this.historyExport.endDate)
                if (this.historyExport.eventCode) {
                    params.set("event_code", this.historyExport.eventCode)
                } else if (this.historyExport.includeSales) {
                    params.set("include_sales", "1")
                }

                const res = await http.get(`/stock/logs?${params.toString()}`)
                const logs = (res.data.result || []).filter((log) => String(log.warehouse_id) === String(this.warehouse))
                const rows = this.flattenStockHistoryRows(logs)
                if (!rows.length) {
                    this.$toast.error("No stock history rows available to export.")
                    return
                }

                const dateRangeLabel =
                    this.historyExport.startDate || this.historyExport.endDate
                        ? `${this.historyExport.startDate || "start"} to ${this.historyExport.endDate || "today"}`
                        : "all dates"
                const eventLabel = this.historyExport.eventCode ? this.historyExport.eventCode.toLowerCase().replace(/_/g, " ") : "all movements"
                downloadCSV(
                    rows,
                    this.exportFileName([this.selectedWarehouseSlugName, "stock movement history", eventLabel, dateRangeLabel]),
                    STOCK_HISTORY_EXPORT_COLUMNS
                )
                this.showHistoryExportModal = false
            } catch (err) {
                console.log("error exporting stock history", err)
                this.$toast.error("Stock history export failed.")
            } finally {
                this.exportingInventory = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "./inventory-manager.scss";
</style>
